import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../index';
import {
    createUpdateDeploymentRequest,
    getAllDeploymentRequests,
    getDeploymentRequestsSummary,
    approveDeploymentRequest,
    denyDeploymentRequest
} from 'api/deployment-requests';
import { store } from 'store';
import { validateDeploymentRequestForm } from 'utils/helpers';

export const initialState = {
    list: [],
    total: 0,
    newRequest: {
        defaultQuizLevel: 'dp',
        work: true,
        study: true,
        file: null
    },
    error: [],
    isLoading: false,
    rowsPerPage: 10,
    currentPage: 1,
    searchQuery: '',
    renewalDateFilter: null,
    summary: {
        active: 0,
        declined: 0,
        isLoading: false
    },
    tags: []
};

const slice = createSlice({
    name: 'deploymentRequests',
    initialState,
    reducers: {
        setDeploymentRequestsList(state, action) {
            state.list = action.payload.data;
            state.total = action.payload.total;
            state.isLoading = false;
        },
        setNewRequest(state, action) {
            state.newRequest = action.payload;
        },
        setIsLoading(state, action) {
            state.isLoading = action.payload;
            // reset error if loading is true;
            if (action.payload === true) state.error = [];
        },
        setErrors(state, action) {
            state.error = action.payload;
        },
        setDeploymentRequestsCurrentPage(state, action) {
            state.currentPage = action.payload;
        },
        setSearchQuery(state, action) {
            state.currentPage = 1; // reset to page 1
            state.searchQuery = action.payload;
        },
        setDeploymentRequestsRowsPerPage(state, action) {
            state.rowsPerPage = action.payload;
        },
        resetDeploymentRequestState() {
            return initialState;
        },

        setDeploymentRequestsSummaryLoading(state, action) {
            state.summary.isLoading = action.payload;
        },

        setDeploymentRequestsSummary(state, action) {
            state.summary = action.payload.data;
            state.summary.isLoading = false;
        },
        setTags(state, action) {
            state.currentPage = 1; // reset to page 1
            state.tags = action.payload;
        }
    }
});

export default slice.reducer;

export function createNewDeploymentRequest() {
    return async () => {
        dispatch(slice.actions.setIsLoading(true));
        const storeData = store.getState();
        const { newRequest } = storeData.deploymentRequests;
        try {
            const validateForm = validateDeploymentRequestForm(newRequest);

            if (!validateForm.isValid) {
                dispatch(slice.actions.setErrors(validateForm.errors));
                dispatch(slice.actions.setIsLoading(false));
                throw new Error('Please fill all the required fields.');
            }

            await createUpdateDeploymentRequest(newRequest);
            dispatch(slice.actions.setNewRequest(initialState.newRequest));
            getListDeploymentRequests();
            getDeploymentRequestSummary();
        } catch (e) {
            console.log('Error encountered');
            dispatch(slice.actions.setIsLoading(false));
            throw new Error(e.message);
        }
    };
}

export const resetDeploymentRequest = () => {
    return async () => {
        await dispatch(slice.actions.setNewRequest(initialState.newRequest));
    };
};

export function fetchDeploymentRequestsList() {
    return async () => {
        await getListDeploymentRequests();
    };
}
export function setNewDeploymentRequest(data) {
    return async () => {
        await dispatch(slice.actions.setNewRequest(data));
    };
}

export function initialLoadDeploymentRequestData() {
    return async () => {
        await dispatch(slice.actions.resetDeploymentRequestState());
        await getListDeploymentRequests();
    };
}

export function deploymentRequestsCurrentPage(val) {
    return async () => {
        await dispatch(slice.actions.setDeploymentRequestsCurrentPage(val));
        await getListDeploymentRequests();
    };
}

export function deploymentRequestsRowsPerPage(val) {
    return async () => {
        dispatch(slice.actions.setDeploymentRequestsRowsPerPage(val));
    };
}

export function searchDeploymentRequestsList(query) {
    return async () => {
        dispatch(slice.actions.setSearchQuery(query));
        await getListDeploymentRequests();
    };
}

export function fetchDeploymentRequestsSummary() {
    return async () => {
        await getDeploymentRequestSummary();
    };
}

export function processApproveRequest() {
    return async () => {
        try {
            const storeData = store.getState();
            const { newRequest } = storeData.deploymentRequests;
            await approveDeploymentRequest(newRequest?._id);
            getListDeploymentRequests();
            getDeploymentRequestSummary();
        } catch (err) {
            console.log(err);
            dispatch(slice.actions.setDeploymentRequestsSummaryLoading(false));
            throw new Error(err.message);
        }
    };
}

export function processDenyRequest(reason) {
    return async () => {
        try {
            const storeData = store.getState();
            const { newRequest } = storeData.deploymentRequests;
            await denyDeploymentRequest({
                id: newRequest?._id,
                reason
            });
            getListDeploymentRequests();
            getDeploymentRequestSummary();
        } catch (err) {
            console.log(err);
            dispatch(slice.actions.setDeploymentRequestsSummaryLoading(false));
            throw new Error(err.message);
        }
    };
}

// clients list advanced filtering
export function searchAdvancedFiltering(tags) {
    return async () => {
        dispatch(slice.actions.setTags(tags));
        await getListDeploymentRequests();
    };
}

async function getListDeploymentRequests() {
    try {
        dispatch(slice.actions.setIsLoading(true));
        const storeData = store.getState();
        const { currentPage, rowsPerPage, searchQuery, renewalDateFilter, tags } = storeData.deploymentRequests;
        const deploymentRequests = await getAllDeploymentRequests({
            perPage: rowsPerPage,
            pageNo: currentPage,
            query: searchQuery,
            startDate: renewalDateFilter?.startDate || '',
            endDate: renewalDateFilter?.endDate || '',
            tags
        });
        dispatch(slice.actions.setDeploymentRequestsList(deploymentRequests?.data));
    } catch (err) {
        console.log(err);
        dispatch(slice.actions.setIsLoading(false));
        throw new Error(err.message);
    }
}

async function getDeploymentRequestSummary() {
    try {
        dispatch(slice.actions.setDeploymentRequestsSummaryLoading(true));
        const summary = await getDeploymentRequestsSummary();
        dispatch(slice.actions.setDeploymentRequestsSummary(summary));
    } catch (err) {
        console.log(err);
        dispatch(slice.actions.setDeploymentRequestsSummaryLoading(false));
        throw new Error(err.message);
    }
}
